/**
 * Deal Intake Notes — mirror a deal's per-service intake details into the
 * unified `notes` collection.
 *
 * WHY THIS EXISTS
 * ---------------
 * When a deal is created, the salesperson fills typed intake fields per
 * service (Surveillance camera count + details, Starlink kit/model/plan,
 * VoIP handsets + porting, the per-service "Notes" box, etc.). Those values
 * were only ever stored on `deal.serviceModules[cat].intakeFields`. They show
 * in the deal's collapsible "Intake Details" section but NEVER reached:
 *   - the scheduler side panel (`UnifiedNotesPanel`), or
 *   - the tech's field view (`JobBriefCard` / `JobNotesTimeline`),
 * because both of those read the unified `notes` collection (rolled up
 * job + parent deal via `useRolledUpJobNotes` / `subscribeRolledUpNotes`),
 * NOT the deal's raw intake fields.
 *
 * Seeding one deal-scoped note per service (with content) makes the intake
 * surface everywhere the deal/job appears via the EXISTING rollup — no new
 * read-side wiring, no new Firestore index. The general "Call Notes"
 * (`deal.notes`) already flows to the schedule + field via the job
 * office-brief (`job.notes`), so it is intentionally NOT re-seeded here
 * (re-seeding it would double-display on those surfaces).
 *
 * IDEMPOTENCY
 * -----------
 * `seedDealIntakeNotes` claims the `intakeNotesSeeded` flag inside the same
 * transaction that writes the note docs, so concurrent callers (the creation
 * wizard + the job-creation safety net) mirror exactly once — never twice.
 */

import { db } from '@/firebase';
import { collection, doc, runTransaction } from 'firebase/firestore';
import { DealCategory, DEAL_CATEGORY_LABELS } from '@/types/theo-workflow.types';
import type { Deal } from '@/types/theo-workflow.types';
import type { Note } from '@/types';
import { SERVICE_CONFIGS } from '@/data/service-configs';
import { getTenantIdSync } from '@/lib/tenant';

export interface IntakeNoteDraft {
  /** Service the note belongs to (undefined for the general call note). */
  category?: DealCategory;
  content: string;
}

/** Humanize an unlabelled intake key — `starlinkEmail` → "Starlink Email". */
function humanizeKey(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Format a single intake value for display. Returns null when empty. */
function formatValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (Array.isArray(value)) {
    const parts = value.map((v) => formatScalar(v)).filter((s): s is string => !!s);
    return parts.length ? parts.join(', ') : null;
  }
  // Defensive: intake fields are scalars/arrays today, but never let a future
  // object-shaped field render as the useless "[object Object]" — stringify it.
  if (typeof value === 'object') {
    try {
      const s = JSON.stringify(value);
      return s && s !== '{}' && s !== '[]' ? s : null;
    } catch {
      return null;
    }
  }
  const str = String(value).trim();
  return str.length ? str : null;
}

/** Format one array element to a non-empty string, or '' to drop it. */
function formatScalar(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v);
    } catch {
      return '';
    }
  }
  return String(v).trim();
}

/**
 * Build the intake note content for each selected service that has any
 * non-empty intake field. Pure — exported for testing.
 *
 * Fields are emitted in service-config order (so labels read naturally),
 * then any captured keys not in the config are appended with a humanized
 * label so nothing the intake modal captured is silently dropped.
 */
export function buildIntakeNoteContents(
  deal: Pick<Deal, 'selectedServices' | 'serviceModules' | 'notes'>,
): IntakeNoteDraft[] {
  const drafts: IntakeNoteDraft[] = [];

  // General call note from the intake form (deal.notes) — surfaced as its own
  // timestamped note so it lives in the timeline like everything else, instead
  // of being buried in the job's boilerplate office brief.
  const callNotes = (deal.notes ?? '').trim();
  if (callNotes) {
    drafts.push({ content: `Call notes\n${callNotes}` });
  }

  // Dedupe — a service listed twice must not yield two identical notes.
  const services = Array.from(new Set(deal.selectedServices ?? []));

  for (const category of services) {
    const moduleData = deal.serviceModules?.[category];
    const fields = (moduleData?.intakeFields as unknown as Record<string, unknown>) ?? {};
    const config = SERVICE_CONFIGS[category];
    const label = DEAL_CATEGORY_LABELS[category] ?? config?.label ?? String(category);

    const lines: string[] = [];
    const seen = new Set<string>();

    // 1. Config-defined fields first — ordered and properly labelled.
    for (const def of config?.intakeFieldDefs ?? []) {
      seen.add(def.key);
      const formatted = formatValue(fields[def.key]);
      if (formatted !== null) lines.push(`${def.label}: ${formatted}`);
    }

    // 2. Any leftover captured keys (custom fields / future additions).
    for (const key of Object.keys(fields)) {
      if (seen.has(key)) continue;
      const formatted = formatValue(fields[key]);
      if (formatted !== null) lines.push(`${humanizeKey(key)}: ${formatted}`);
    }

    if (lines.length === 0) continue; // nothing worth surfacing for this service

    drafts.push({
      category,
      content: `${label} — intake details\n${lines.join('\n')}`,
    });
  }

  return drafts;
}

/**
 * Mirror a deal's per-service intake details into the unified `notes`
 * collection — exactly once per deal. Safe to call from multiple paths
 * (the creation wizard + job creation); the `intakeNotesSeeded` flag is
 * claimed transactionally alongside the note writes.
 *
 * Returns the number of notes written (0 if already seeded or nothing to
 * seed). Best-effort by design: never throws, so callers can fire-and-forget
 * without risking the deal/job creation flow.
 */
export async function seedDealIntakeNotes(
  dealId: string,
  actorId: string,
  actorName: string,
): Promise<number> {
  if (!dealId) return 0;
  const dealRef = doc(db, 'deals', dealId);

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(dealRef);
    if (!snap.exists()) return 0;

    const deal = { id: snap.id, ...snap.data() } as Deal;
    if (deal.intakeNotesSeeded) return 0; // already mirrored — no-op

    const drafts = buildIntakeNoteContents(deal);
    const now = new Date().toISOString();
    // Date the notes to when the intake was actually captured (deal creation)
    // so the timeline reads true to life — the install job that triggers the
    // seed can be created days later, and back-dating keeps the chronology
    // honest. createdAt may be an ISO string (current convention) or a legacy
    // Firestore Timestamp; coerce both, fall back to seed time when absent.
    const rawCreated = (deal as unknown as { createdAt?: unknown }).createdAt;
    const capturedAt =
      typeof rawCreated === 'string'
        ? rawCreated
        : rawCreated && typeof (rawCreated as { toDate?: () => Date }).toDate === 'function'
          ? (rawCreated as { toDate: () => Date }).toDate().toISOString()
          : now;

    // Claim the flag even when there's no content, so a deal with empty
    // intake isn't re-scanned (and re-transacted) on every future job
    // creation.
    tx.update(dealRef, { intakeNotesSeeded: true, intakeNotesSeededAt: now });

    if (drafts.length === 0) return 0;

    const tenantId = deal.tenantId ?? getTenantIdSync();

    for (const draft of drafts) {
      const noteRef = doc(collection(db, 'notes'));
      const note: Omit<Note, 'id'> = {
        tenantId,
        entityType: 'deal',
        entityId: dealId,
        content: draft.content,
        mentions: [],
        source: 'deal_workspace',
        createdBy: actorId,
        createdByName: actorName,
        pinned: false,
        createdAt: capturedAt,
        updatedAt: capturedAt,
      };
      tx.set(noteRef, note);
    }

    return drafts.length;
  }).catch((err) => {
    console.warn('[deal-intake-notes] seed failed:', err);
    return 0;
  });
}
